import React, { useCallback, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@ui/Icon';
import {
  isAllowedClaudeModel,
  isAllowedGeminiTextModel,
  isAllowedGrokModel,
  isAllowedOpenAiModel,
} from '@shared/models/ai/ai-models.model';
import { getOpenAiReasoningSupport } from '@shared/models/ai/open-ai-reasoning.model';
import { isAdmin } from '@shared/helpers/user.helpers';
import { useUser } from '@client/hooks/use-user.hooks';
import { useErrorHandler } from '@client/hooks/use-error-handler.hooks';
import { reportDesignPersistError } from '../helpers/design-snapshot-persist.helpers';
import type { PdfGeometryImportAi } from '@shared/models/design/pdf-geometry-import.model';
import { getGeometryImportSupport } from '@shared/helpers/design/geometry-import-capabilities.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  buildGeometryImportAiForProvider,
  buildGeometryImportDialogDefaults,
  buildOpenAiGeometryImportAi,
  geometryImportModelsForProvider,
  type GeometryImportRequest,
} from '../helpers/geometry-import-dialog.helpers';

type PdfGeometryImportDialogProps = {
  open: boolean;
  file: File;
  onClose: () => void;
  onImport: (input: GeometryImportRequest) => Promise<void>;
};

type Provider = PdfGeometryImportAi['provider'];

/**
 * Import geometry dialog. Extra instructions are always visible; Advanced
 * (admins) can edit the system prompt, provider, and model.
 * The server still requires structured JSON matching the import schema.
 */
export const PdfGeometryImportDialog: React.FC<
  PdfGeometryImportDialogProps
> = ({ open, file, onClose, onImport }) => {
  const { classes } = useStyles();
  const user = useUser();
  const errorHandler = useErrorHandler();
  const canEditAdvanced = isAdmin(user);
  const defaults = useMemo(
    () =>
      buildGeometryImportDialogDefaults({
        filename: file.name,
        mimeType: file.type,
      }),
    [file.name, file.type],
  );

  const [loading, setLoading] = useState(false);
  const [ai, setAi] = useState<PdfGeometryImportAi>(defaults.ai);
  const [extraInstructions, setExtraInstructions] = useState(
    defaults.extraInstructions,
  );
  const [systemPrompt, setSystemPrompt] = useState(defaults.systemPrompt);
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);

  const models = geometryImportModelsForProvider(ai.provider);
  const support = getGeometryImportSupport({
    provider: ai.provider,
    model: ai.model,
    filename: file.name,
    mimeType: file.type,
  });
  const reasoningSupport =
    ai.provider === 'openai' ? getOpenAiReasoningSupport(ai.model) : undefined;

  const handleCancel = useCallback(() => {
    if (loading) return; // Lock dialog while importing.
    onClose();
  }, [loading, onClose]);

  const handleProviderChange = (nextProvider: Provider) => {
    setAi(buildGeometryImportAiForProvider(nextProvider, defaults));
  };

  const handleModelChange = (value: unknown) => {
    switch (ai.provider) {
      case 'openai':
        if (!isAllowedOpenAiModel(value)) return;
        setAi(buildOpenAiGeometryImportAi(value, ai.reasoningEffort));
        return;
      case 'gemini':
        if (!isAllowedGeminiTextModel(value)) return;
        setAi({ provider: 'gemini', model: value });
        return;
      case 'claude':
        if (!isAllowedClaudeModel(value)) return;
        setAi({ provider: 'claude', model: value });
        return;
      case 'grok':
        if (!isAllowedGrokModel(value)) return;
        setAi({ provider: 'grok', model: value });
        return;
      default:
        assertUnreachable(ai);
    }
  };

  /**
   * Updates reasoning effort when the selected OpenAI model accepts that value.
   */
  const handleReasoningChange = (value: unknown) => {
    if (ai.provider !== 'openai') return;
    setAi(buildOpenAiGeometryImportAi(ai.model, value));
  };

  const handleImport = useCallback(async () => {
    if (loading) return;
    try {
      setLoading(true);
      await onImport({
        file,
        ai,
        extraInstructions: extraInstructions.trim(),
        systemPrompt: systemPrompt.trim(),
      });
      setLoading(false);
      onClose();
    } catch (error) {
      setLoading(false);
      reportDesignPersistError(error, errorHandler);
    }
  }, [
    ai,
    errorHandler,
    extraInstructions,
    file,
    loading,
    onClose,
    onImport,
    systemPrompt,
  ]);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Import geometry</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.dialogHint} variant="body2">
          {
            'Extract the heated ground-floor footprint (no balconies) and storey stack. PDF is most reliable; .dxf, .ifc and .ifczip are exploded to CAD source text. .dwg and .rvt must be exported to PDF or DXF first.'
          }
        </Typography>
        <Typography className={classes.fileName} variant="body2">
          {file.name}
        </Typography>
        <TextField
          className={classes.dialogField}
          label="Extra instructions (optional)"
          value={extraInstructions}
          onChange={(e) => setExtraInstructions(e.target.value)}
          placeholder="e.g. Scale 1:200. Use the main building outline only."
          multiline
          minRows={2}
          fullWidth
          disabled={loading}
        />
        {support.message && (
          <Typography className={classes.supportWarning} variant="body2">
            {support.message}
          </Typography>
        )}

        {canEditAdvanced && (
          <div className={classes.advancedSection}>
            <Button
              className={classes.advancedToggle}
              onClick={() => setIsAdvancedExpanded((expanded) => !expanded)}
              aria-expanded={isAdvancedExpanded}
              disabled={loading}
              startIcon={
                <Icon
                  category="mui"
                  icon={
                    isAdvancedExpanded
                      ? 'KeyboardArrowDown'
                      : 'KeyboardArrowRight'
                  }
                />
              }
            >
              Advanced
            </Button>
            <Collapse in={isAdvancedExpanded} unmountOnExit>
              <div className={classes.advancedContent}>
                <Typography className={classes.dialogHint} variant="body2">
                  {
                    'The system prompt is sent as written. The server still requires structured JSON matching the import schema, regardless of what you type here.'
                  }
                </Typography>
                <TextField
                  className={classes.dialogField}
                  label="System prompt"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  multiline
                  minRows={8}
                  fullWidth
                  disabled={loading}
                />

                <div className={classes.dialogRow}>
                  <FormControl className={classes.dialogField} fullWidth>
                    <InputLabel id="pdf-provider-label">Provider</InputLabel>
                    <Select
                      labelId="pdf-provider-label"
                      label="Provider"
                      value={ai.provider}
                      onChange={(e) => {
                        const next = parseProvider(e.target.value);
                        if (!next) return;
                        handleProviderChange(next);
                      }}
                      disabled={loading}
                    >
                      <MenuItem value="gemini">Google Gemini</MenuItem>
                      <MenuItem value="openai">Open AI</MenuItem>
                      <MenuItem value="claude">Anthropic Claude</MenuItem>
                      <MenuItem value="grok">xAI Grok</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl className={classes.dialogField} fullWidth>
                    <InputLabel id="pdf-model-label">Model</InputLabel>
                    <Select
                      labelId="pdf-model-label"
                      label="Model"
                      value={ai.model}
                      onChange={(e) => handleModelChange(e.target.value)}
                      disabled={loading}
                    >
                      {models.map((model) => (
                        <MenuItem key={model} value={model}>
                          {model}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                {reasoningSupport && ai.provider === 'openai' && (
                  <FormControl className={classes.dialogField} fullWidth>
                    <InputLabel id="pdf-reasoning-label">Reasoning</InputLabel>
                    <Select
                      labelId="pdf-reasoning-label"
                      label="Reasoning"
                      value={
                        ai.reasoningEffort ?? reasoningSupport.defaultEffort
                      }
                      onChange={(e) => handleReasoningChange(e.target.value)}
                      disabled={loading}
                    >
                      {reasoningSupport.efforts.map((effort) => (
                        <MenuItem key={effort} value={effort}>
                          {effort}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </div>
            </Collapse>
          </div>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={loading || support.state === 'unsupported'}
        >
          {loading ? (
            <CircularProgress size={18} thickness={5} color="inherit" />
          ) : (
            'Import'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const parseProvider = (value: unknown): Provider | undefined => {
  if (
    value === 'openai' ||
    value === 'gemini' ||
    value === 'claude' ||
    value === 'grok'
  ) {
    return value;
  }
  return undefined;
};

const useStyles = makeStyles()((theme) => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  dialogRow: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  dialogField: {
    width: '100%',
  },
  dialogActions: {
    padding: theme.spacing(1.5, 3, 2),
  },
  dialogHint: {
    color: theme.palette.text.secondary,
  },
  fileName: {
    color: theme.palette.text.secondary,
    wordBreak: 'break-word',
  },
  supportWarning: {
    color: theme.palette.warning.main,
  },
  advancedSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  advancedToggle: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  advancedContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));
